import React, { useCallback, useSyncExternalStore } from "react";
import Dialog from "../../common/Dialog/Dialog";

// Types
export type PersistentComponent<P = any> = React.FC<PersistentComponentProps<P>>;

export type PersistentComponentProps<P = any> = P & {
  close: () => void;
};

export const PERSISTENT_COMPONENT_LAYER = Symbol("persistent-component-layer");

export type PersistentComponentLayer = {
  id: string;
  [PERSISTENT_COMPONENT_LAYER]: true;
};

export const createPersistentComponentLayer = (id: string): PersistentComponentLayer => ({
  id,
  [PERSISTENT_COMPONENT_LAYER]: true,
});

type Component<P = any> = {
  id: string;
  layer: PersistentComponentLayer;
  component: PersistentComponent<P>;
};

export interface PushComponentOptions<P = any> {
  layer: PersistentComponentLayer;
  component: PersistentComponent<P>;
}

export interface PopComponentOptions {
  layer: PersistentComponentLayer;
}

export type UsePersistentComponents = {
  /** Remove the top most component from the stack for a given layer */
  popComponent: (options: PopComponentOptions) => void;
  /** Pushes a component onto the stack for a given layer */
  pushComponent: <P = any>(options: PushComponentOptions<P>) => string;
  /** Removes all components from the stack for a given layer */
  clearComponents: (layer: PersistentComponentLayer) => void;
  /** Removes a specific component by id */
  removeComponent: (layer: PersistentComponentLayer, id: string) => void;
};

// Store
type Listener = () => void;

class ComponentStore {
  private __components: Component[] = [];
  private __listeners: Set<Listener> = new Set();
  private __portCache: Map<string, Component[]> = new Map();
  private __portCacheIds: Map<string, string> = new Map();

  getComponentsForLayer(layer: PersistentComponentLayer): Component[] {
    const layerId = layer.id;
    // Calculate current component IDs for this layer
    const currentIds = this.__components
      .filter((c) => c.layer.id === layerId)
      .map((c) => c.id)
      .sort()
      .join(",");

    const lastIds = this.__portCacheIds.get(layerId) || "";

    // Only rebuild if IDs changed
    if (currentIds !== lastIds) {
      const newComponents = this.__components.filter((c) => c.layer.id === layerId);
      this.__portCache.set(layerId, newComponents);
      this.__portCacheIds.set(layerId, currentIds);
    }

    // Always return the cached array (stable reference)
    if (!this.__portCache.has(layerId)) {
      this.__portCache.set(layerId, []);
      this.__portCacheIds.set(layerId, "");
    }

    return this.__portCache.get(layerId)!;
  }

  subscribe(listener: Listener): () => void {
    this.__listeners.add(listener);
    return () => {
      this.__listeners.delete(listener);
    };
  }

  private notify() {
    // Invalidate all port caches
    this.__portCache.clear();
    this.__portCacheIds.clear();
    this.__listeners.forEach((listener) => listener());
  }

  pushComponent(component: Component) {
    this.__components = this.__components.concat(component);
    this.notify();
  }

  popComponent(layer: PersistentComponentLayer) {
    const layerComponents = this.__components.filter((c) => c.layer.id === layer.id);
    if (layerComponents.length > 0) {
      const lastComponent = layerComponents[layerComponents.length - 1];
      this.__components = this.__components.filter((c) => c.id !== lastComponent.id);
      this.notify();
    }
  }

  removeComponent(layer: PersistentComponentLayer, id: string) {
    this.__components = this.__components.filter((c) => !(c.layer.id === layer.id && c.id === id));
    this.notify();
  }

  clearComponents(layer: PersistentComponentLayer) {
    this.__components = this.__components.filter((c) => c.layer.id !== layer.id);
    this.notify();
  }
}

const componentStore = new ComponentStore();

// Hook
/**
 * Hook to manage persistent components.
 * Provides functions to push, pop, and clear components from a global stack.
 *
 * @example
 * ```tsx
 * const { pushComponent, popComponent } = usePersistentComponents();
 *
 * const openMyComponent = () => {
 *   pushComponent({ layer: MY_LAYER, component: MyComponent });
 * };
 * ```
 */
export const usePersistentComponents = (): UsePersistentComponents => {
  const popComponent = useCallback(({ layer }: PopComponentOptions) => {
    componentStore.popComponent(layer);
  }, []);

  const pushComponent = useCallback(<P = any,>({ layer, component }: PushComponentOptions<P>): string => {
    const id = `component-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    componentStore.pushComponent({ component, id, layer });
    return id;
  }, []);

  const clearComponents = useCallback((layer: PersistentComponentLayer) => {
    componentStore.clearComponents(layer);
  }, []);

  const removeComponent = useCallback((layer: PersistentComponentLayer, id: string) => {
    componentStore.removeComponent(layer, id);
  }, []);

  return { popComponent, pushComponent, clearComponents, removeComponent };
};

// Component
export interface PersistentComponentsProps {
  layer: PersistentComponentLayer;
}

/**
 * Component that renders all persistent components for a given port.
 * This should be placed at the root of your application, typically in your main layout.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <>
 *       <PersistentComponents layer={RELEASE_LAYER} />
 *       <YourAppContent />
 *     </>
 *   );
 * }
 * ```
 */
export const PersistentComponents = React.memo(({ layer }: PersistentComponentsProps) => {
  const components = useSyncExternalStore(
    (listener) => componentStore.subscribe(listener),
    () => componentStore.getComponentsForLayer(layer)
  );

  return (
    <>
      {components.map((component) => (
        <ComponentItem key={component.id} component={component} layer={layer} />
      ))}
    </>
  );
});

PersistentComponents.displayName = "PersistentComponents";

interface ComponentItemProps {
  component: Component;
  layer: PersistentComponentLayer;
}

const ComponentItem = React.memo(({ component, layer }: ComponentItemProps) => {
  const { component: Component, id } = component;

  const handleClose = () => {
    componentStore.removeComponent(layer, id);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <Component close={handleClose} />
    </Dialog>
  );
});

ComponentItem.displayName = "ComponentItem";
